import { useEffect, useState, type ReactElement } from 'react';
import type { ApiClient, RelationshipProfileDTO } from './auth/apiClient.js';

/**
 * ORBIT (ORBIT-10) — the passive relationship panel on a contact page. Reads the precomputed
 * `relationshipProfiles` doc (no live compute, D-14) and surfaces: comms state (last touch, cadence +
 * overdue, an owed-reply badge), the recent-comms summary, external news (with citations), a
 * commitments checklist, and the introducer confirm/dismiss control. Passive — no nudges (D-4).
 */
function daysAgo(iso: string | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  const d = Math.floor(ms / 86_400_000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

export function RelationshipSection({ api, entityKey }: { api: ApiClient; entityKey: string }): ReactElement | null {
  const [profile, setProfile] = useState<RelationshipProfileDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void api
      .getRelationshipProfile(entityKey)
      .then((p) => {
        if (live) setProfile(p);
      })
      .catch(() => {
        if (live) setProfile(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [api, entityKey]);

  if (loading) return <section className="lapo-relationship"><p className="lapo-decisions-empty">Loading relationship…</p></section>;
  if (!profile) return null; // No profile yet (not in the active set) — stay quiet.

  const confirm = async (): Promise<void> => {
    setBusy(true);
    try {
      setProfile(await api.confirmIntroducer(entityKey));
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async (): Promise<void> => {
    setBusy(true);
    try {
      setProfile(await api.dismissIntroducer(entityKey));
    } finally {
      setBusy(false);
    }
  };

  const cadence =
    profile.cadenceMedianDays != null
      ? `~${profile.cadenceMedianDays}d cadence${profile.overdue ? ` · overdue${profile.overdueByDays ? ` ${profile.overdueByDays}d` : ''}` : ''}`
      : 'cadence unknown';

  return (
    <section className="lapo-relationship">
      <h2 className="lapo-relationship-title">Relationship</h2>

      <div className="lapo-relationship-badges">
        <span className="lapo-relationship-badge">Last touch: {daysAgo(profile.lastTouchAt)}{profile.lastTouchChannel ? ` (${profile.lastTouchChannel})` : ''}</span>
        <span className={`lapo-relationship-badge${profile.overdue ? ' lapo-relationship-badge-warn' : ''}`}>{cadence}</span>
        {profile.owedReply && <span className="lapo-relationship-badge lapo-relationship-badge-owed">You owe a reply</span>}
        <span className="lapo-relationship-badge">{profile.category}</span>
      </div>

      {profile.recentCommsSummary && <p className="lapo-relationship-summary">{profile.recentCommsSummary}</p>}

      {profile.introducer && (profile.introducer.name || profile.introducer.email) && !profile.introducer.dismissed && (
        <div className="lapo-relationship-introducer">
          {profile.introducer.confirmed ? (
            <span>Introduced by {profile.introducer.name || profile.introducer.email}</span>
          ) : (
            <span>
              Introduced by {profile.introducer.name || profile.introducer.email}?{' '}
              <button type="button" disabled={busy} onClick={confirm}>Yes</button>{' '}
              <button type="button" disabled={busy} onClick={dismiss}>No</button>
            </span>
          )}
        </div>
      )}

      {profile.commitments && profile.commitments.length > 0 && (
        <div className="lapo-relationship-commitments">
          <h3>Commitments</h3>
          <ul>
            {profile.commitments.map((c, i) => (
              <li key={i} className={c.done ? 'lapo-relationship-done' : ''}>
                <span className="lapo-relationship-owe">{c.direction === 'i-owe' ? 'You' : 'They'}:</span> {c.text}
                {c.done ? ' ✓' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile.externalNews && profile.externalNews.length > 0 && (
        <div className="lapo-relationship-news">
          <h3>In the news</h3>
          <ul>
            {profile.externalNews.map((n, i) => (
              <li key={i}>
                <a href={n.url} target="_blank" rel="noreferrer noopener">{n.headline}</a>
                {n.source ? ` — ${n.source}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
